import { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  useBreakpointValue,
  useColorModeValue,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Skeleton,
  AspectRatio,
  Flex,
  Center,
} from "@chakra-ui/react";
import { animated, useSpring } from "@react-spring/web";
import { Helmet } from "react-helmet-async";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import { ViewIcon, StarIcon } from "@chakra-ui/icons";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { Link } from "react-router-dom";
import pathCongressi from "../../assets/images/congressi & convention/path.json";
import pathFiere from "../../assets/images/fiere & showroom/path.json";
import pathCene from "../../assets/images/cene di gala/path.json";
import pathEventi from "../../assets/images/eventi/path.json";

const categories = [
  {
    categoria: "eventi",
    path: pathEventi,
    keyword: "eventi Firenze",
    description:
      "Service luci per eventi a Firenze con soluzioni illuminotecniche professionali",
    color: "orange.500",
  },
  {
    categoria: "congressi & convention",
    path: pathCongressi,
    keyword: "congressi Firenze",
    description:
      "Noleggio luci per congressi e convention a Firenze con attrezzature all'avanguardia",
    color: "blue.500",
  },
  {
    categoria: "cene di gala",
    path: pathCene,
    keyword: "cene di gala Firenze",
    description:
      "Illuminazione per cene di gala a Firenze con atmosfere eleganti e ricercate",
    color: "purple.500",
  },
  {
    categoria: "fiere & showroom",
    path: pathFiere,
    keyword: "fiere Firenze",
    description:
      "Servizio luci per fiere e showroom a Firenze con soluzioni personalizzate",
    color: "teal.500",
  },
];

const Gallery = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [activeCategory, setActiveCategory] = useState(0);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTablet = useBreakpointValue({ base: true, md: true, lg: false });

  // Colori per dark mode
  const bgColor = useColorModeValue(
    "rgba(248, 250, 252, 0.8)", // light mode con opacità
    "rgba(26, 32, 44, 0.9)", // dark mode con opacità
  );
  const textColor = useColorModeValue("gray.600", "gray.800");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const springs = useSpring({
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    config: { tension: 280, friction: 60 },
  });

  const handleImageLoad = (category, index) => {
    setLoadedImages((prev) => ({
      ...prev,
      [`${category}-${index}`]: true,
    }));
  };

  const openGallery = (categoryIndex, imageIndex = 0) => {
    setSelectedCategory(categoryIndex);
    setSelectedImageIndex(imageIndex);
    onOpen();
  };

  // Swiper configuration for main carousels
  const mainSwiperConfig = (categoryIndex) => ({
    modules: [Navigation, Pagination, Autoplay, EffectFade],
    spaceBetween: 0,
    slidesPerView: 1,
    navigation: {
      nextEl: `.swiper-button-next-${categoryIndex}`,
      prevEl: `.swiper-button-prev-${categoryIndex}`,
    },
    onBeforeInit: (swiper) => {
      swiper.params.navigation.prevEl = `.swiper-button-prev-${categoryIndex}`;
      swiper.params.navigation.nextEl = `.swiper-button-next-${categoryIndex}`;
    },
    pagination: {
      clickable: true,
      dynamicBullets: true,
    },
    autoplay: {
      delay: 4000,
      disableOnInteraction: false,
    },
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    loop: true,
    onSlideChange: (swiper) => {
      // Optional: add any slide change logic
    },
  });

  // Swiper configuration for modal
  const modalSwiperConfig = {
    modules: [Navigation, Pagination, EffectFade],
    spaceBetween: 0,
    slidesPerView: 1,
    navigation: !isMobile,
    pagination: {
      clickable: true,
      dynamicBullets: true,
    },
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    initialSlide: selectedImageIndex,
  };

  return (
    <animated.div style={{ ...springs }}>
      <Helmet>
        <title>
          Galleria Lavori | Service Luci Firenze | Noleggio Luci per Eventi -
          Controluce
        </title>
        <meta
          name="description"
          content="Guarda la nostra galleria di lavori con service luci a Firenze. Servizio noleggio luci professionale per eventi, congressi, cene di gala e fiere. 36 anni di esperienza."
        />
        <meta
          name="keywords"
          content="Firenze, service luci, servizio noleggio luci, galleria lavori, eventi Firenze, noleggio luci Firenze, illuminazione eventi, congressi Firenze, cene di gala Firenze"
        />
        <link
          rel="canonical"
          href="https://www.controluceilluminazione.it/gallery"
        />
      </Helmet>

      {/* Hero Section */}
      <Box id={"workGallery"} bg={bgColor} pt={{ base: 8, lg: "2em" }}>
        <Container maxW="container.xl" px={{ base: 4, md: 8, lg: 12 }}>
          <VStack spacing={6} align="center" textAlign="center">
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              color={textColor}
              lineHeight="1.2"
            >
              La Nostra Galleria Lavori
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={secondaryTextColor}
              maxW="50em"
              lineHeight="1.8"
            >
              Esplora i nostri progetti di illuminazione per eventi a Firenze.
              Ogni immagine racconta la qualità del nostro{" "}
              <Text as={"strong"} textColor={"orange.500"}>
                service luci professionale
              </Text>{" "}
              e l'attenzione ai dettagli che ci contraddistingue dal 1989.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Gallery Categories */}
      {categories.map((category, categoryIndex) => (
        <Box
          key={categoryIndex}
          pb={{ base: "3.5em", lg: "5em" }}
          pt={{
            base: categoryIndex !== 0 ? "3.5em" : "0.2em",
            lg: categoryIndex !== 0 && "5em",
          }}
          bg={categoryIndex % 2 === 0 ? bgColor : "transparent"}
          position="relative"
        >
          <Container maxW="container.xl" px={{ base: 4, md: 8, lg: 12 }}>
            {/* Category Header */}
            <VStack spacing={4} align="center" mb={{ base: 6, lg: 8 }}>
              <Heading
                as="h2"
                size="lg"
                color={categoryIndex % 2 === 0 && textColor}
                textAlign="center"
              >
                {category.categoria.charAt(0).toUpperCase() +
                  category.categoria.slice(1)}
              </Heading>

              <Text
                color={categoryIndex % 2 === 0 && secondaryTextColor}
                textAlign="center"
                maxW="37.5em"
                fontSize="lg"
              >
                {category.description}
              </Text>
            </VStack>

            {/* Main Carousel */}
            <Box position="relative" mb={{ base: 6, lg: 8 }}>
              <Swiper {...mainSwiperConfig(categoryIndex)}>
                {category.path.slice(0, 8).map((item, imageIndex) => (
                  <SwiperSlide key={imageIndex}>
                    <Box
                      position="relative"
                      borderRadius={{ base: "lg", lg: "xl" }}
                      overflow="hidden"
                      cursor="pointer"
                      onClick={() => openGallery(categoryIndex, imageIndex)}
                      _hover={{
                        "& .image-overlay": {
                          opacity: 1,
                        },
                        "& img": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <AspectRatio ratio={16 / 9}>
                        <Skeleton
                          isLoaded={
                            loadedImages[`${category.categoria}-${imageIndex}`]
                          }
                          startColor="gray.200"
                          endColor="gray.300"
                        >
                          <Image
                            src={require(
                              `../../assets/images/${category.categoria}/${item.imageFileName}`,
                            )}
                            alt={item.altImage}
                            objectFit="cover"
                            w="100%"
                            h="100%"
                            onLoad={() =>
                              handleImageLoad(category.categoria, imageIndex)
                            }
                            loading="lazy"
                            transition="transform 0.7s ease-in-out"
                          />
                        </Skeleton>
                      </AspectRatio>

                      {/* Image Overlay */}
                      <Box
                        className="image-overlay"
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)"
                        opacity={0}
                        transition="opacity 0.3s"
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        p={{ base: 4, lg: 6 }}
                      >
                        <VStack align="start" spacing={2}>
                          <HStack align={"center"}>
                            <ViewIcon color="orange.300" />
                            <Text color="orange.200" fontSize="sm" mb={0}>
                              Clicca per ingrandire
                            </Text>
                          </HStack>
                        </VStack>
                      </Box>
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            {/* View All Button */}
            <Center>
              <Button
                colorScheme="orange"
                // variant="outline"
                size={{ base: "md", xl: "lg" }}
                onClick={() => openGallery(categoryIndex)}
                rightIcon={<ViewIcon color={"gray.100"} mt={"0.1em"} />}
                // borderWidth="2px"
                _hover={{
                  bg: "orange.500",
                  color: "gray.100",
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
                transition="all 0.3s"
              >
                Vedi tutti
              </Button>
            </Center>
          </Container>
        </Box>
      ))}

      {/* Final CTA */}
      <Box py={{ base: 12, lg: 20 }}>
        <Container maxW="container.xl" px={{ base: 4, md: 8, lg: 12 }}>
          <VStack
            spacing={8}
            align="center"
            textAlign="center"
            bg="linear-gradient(135deg, rgba(255, 179, 0, 0.4) 0%, rgba(255, 143, 0, 0.3) 100%)"
            borderRadius="2xl"
            p={{ base: 8, lg: 12 }}
            border="1px solid"
            borderColor="orange.200"
          >
            <Heading as="h2" size="xl" color={"grey.200"}>
              Pronto a Trasformare il Tuo Evento?
            </Heading>

            <Text fontSize="lg" color={"grey.100"} maxW="600px">
              Ogni progetto nella nostra galleria racconta una storia di
              successo. Lasciati ispirare e contattaci per creare insieme
              l'illuminazione perfetta per il tuo prossimo evento.
            </Text>

            <Button
              as={Link}
              to="/contact"
              colorScheme="orange"
              size="lg"
              bg="linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "2xl",
                bg: "linear-gradient(135deg, #FF8F00 0%, #FF6F00 100%)",
              }}
              transition="all 0.3s"
              px={8}
              py={6}
              fontSize="lg"
              leftIcon={<StarIcon />}
            >
              Richiedi un Preventivo
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Modal for Full Gallery */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="transparent" maxW="100vw" h="100vh" boxShadow="none">
          <ModalCloseButton
            color="white"
            bg="rgba(0,0,0,0.7)"
            _hover={{ bg: "rgba(0,0,0,0.9)" }}
            size="lg"
            position="fixed"
            top={4}
            right={4}
            zIndex={9999}
          />
          <ModalBody p={0}>
            {selectedCategory !== null && (
              <Box position="relative" h="100vh" w="100vw">
                <Swiper
                  {...modalSwiperConfig}
                  style={{ height: "100vh", width: "100vw" }}
                >
                  {categories[selectedCategory].path.map((item, index) => (
                    <SwiperSlide key={index}>
                      <Flex
                        justify="center"
                        align="center"
                        h="100vh"
                        w="100vw"
                        bg="black"
                        position="relative"
                      >
                        <Image
                          src={require(
                            `../../assets/images/${categories[selectedCategory].categoria}/${item.imageFileName}`,
                          )}
                          alt={item.altImage}
                          objectFit="contain"
                          maxH="90vh"
                          maxW="90vw"
                          loading="lazy"
                        />
                      </Flex>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </animated.div>
  );
};

export default Gallery;
